// Cookie consent state, region detection, and persistence — implements the
// behavior specified in the "Cookie Consent Banner: Copy and Behavior" doc.
//
// Region detection here is a best-effort, client-only heuristic based on the
// browser's IANA time zone. It is NOT authoritative geolocation. A
// production rollout should replace this with a server-side / CDN geo
// signal (e.g. a Cloudflare CF-IPCountry header) so region classification
// can't be spoofed or misdetected by a mismatched OS time zone.
export type Region = "eea_uk_ch" | "us" | "row";

export type ConsentCategory = "functional" | "analytics";

export type ConsentCategories = Record<ConsentCategory, boolean>;

export interface ConsentRecord {
  version: number;
  region: Region;
  gpc: boolean;
  categories: ConsentCategories;
  timestamp: string;
}

// Bump when the tools behind a category change, so visitors are re-prompted.
// v2: PostHog + Sentry added under Analytics; Marketing category removed.
export const CONSENT_VERSION = 2;
const STORAGE_KEY = "coharts-cookie-consent";

// IANA time zones for the EEA, UK, and Switzerland — the jurisdictions the
// banner spec treats as opt-in.
const EEA_UK_CH_TIMEZONES = new Set([
  "Europe/Vienna", "Europe/Brussels", "Europe/Sofia", "Europe/Zagreb", "Europe/Nicosia",
  "Europe/Prague", "Europe/Copenhagen", "Europe/Tallinn", "Europe/Helsinki", "Europe/Paris",
  "Europe/Berlin", "Europe/Athens", "Europe/Budapest", "Europe/Dublin", "Europe/Rome",
  "Europe/Riga", "Europe/Vilnius", "Europe/Luxembourg", "Europe/Malta", "Europe/Amsterdam",
  "Europe/Warsaw", "Europe/Lisbon", "Europe/Bucharest", "Europe/Bratislava", "Europe/Ljubljana",
  "Europe/Madrid", "Europe/Stockholm", "Atlantic/Reykjavik", "Europe/Oslo", "Europe/Vaduz",
  "Europe/London", "Europe/Zurich", "Europe/Gibraltar", "Europe/Isle_of_Man", "Europe/Jersey",
  "Europe/Guernsey",
]);

// Every standard US IANA time zone, including territories. Also used as the
// fallback for "America/*" zones not explicitly listed (Canada's America/*
// zones are excluded below), since the banner's US behavior only needs a
// broad "is this the US" signal, not the specific state.
const US_TIMEZONES = new Set([
  "America/New_York", "America/Detroit", "America/Kentucky/Louisville", "America/Kentucky/Monticello",
  "America/Indiana/Indianapolis", "America/Indiana/Vincennes", "America/Indiana/Winamac",
  "America/Indiana/Marengo", "America/Indiana/Petersburg", "America/Indiana/Vevay",
  "America/Indiana/Tell_City", "America/Indiana/Knox", "America/Chicago", "America/Menominee",
  "America/North_Dakota/Center", "America/North_Dakota/New_Salem", "America/North_Dakota/Beulah",
  "America/Denver", "America/Boise", "America/Phoenix", "America/Los_Angeles", "America/Anchorage",
  "America/Juneau", "America/Sitka", "America/Metlakatla", "America/Yakutat", "America/Nome",
  "America/Adak", "Pacific/Honolulu", "America/Puerto_Rico", "America/St_Thomas",
]);

const CANADA_TIMEZONES = new Set([
  "America/Toronto", "America/Vancouver", "America/Edmonton", "America/Winnipeg",
  "America/Halifax", "America/St_Johns", "America/Regina", "America/Whitehorse",
  "America/Yellowknife", "America/Iqaluit", "America/Moncton",
]);

export function detectRegion(): Region {
  let timeZone = "";
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "row";
  }
  if (EEA_UK_CH_TIMEZONES.has(timeZone)) return "eea_uk_ch";
  if (US_TIMEZONES.has(timeZone)) return "us";
  if (CANADA_TIMEZONES.has(timeZone)) return "row";
  if (timeZone.startsWith("America/") && !CANADA_TIMEZONES.has(timeZone)) return "us";
  return "row";
}

export function detectGPC(): boolean {
  return (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

// Default category state per Section 3's table in the banner spec.
export function defaultCategoriesForRegion(region: Region): ConsentCategories {
  if (region === "eea_uk_ch") {
    return { functional: false, analytics: false };
  }
  return { functional: true, analytics: true };
}

export function loadConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(record: ConsentRecord): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage unavailable (private browsing, blocked storage) — the
    // banner will simply reappear next visit, which is an acceptable fallback.
  }
}
